import { api } from "./api";
import { ListRequest, MessageResponse } from "../types/common/commonTypes";
import {
  CompanyCreateGroup,
  PaginatedCompanyResponseGroup,
  CompanyUpdateGroup,
  CompanyType,
  CompanyFullGroup,
} from "../types/companyTypes";

import {
  PersonType,
  PaginatedPersonResponseGroup,
  PersonCreateGroup,
  PersonUpdateGroup,
  PersonFullGroup,
} from "../types/personTypes";

/**
 * Inventory Management Service
 *
 * Provides complete integration with all 12 inventory management API endpoints:
 * - Core lot management (CRUD operations)
 * - FIFO allocation engine
 * - Real-time availability tracking
 * - Lot shipping and movement
 * - Comprehensive inventory summaries
 *
 * Auto-generation: Lot numbers are auto-generated by backend (YY-###### format)
 * Example: 24-000001, 24-000002, etc. (resets annually)
 */
const company_base = "/companies";
export const companyService = {
  // ========================================
  // Core Inventory Lot Management (6 endpoints)
  // ========================================

  /**
   * Create a new inventory lot
   * POST /api/v1/inventory/lots
   * Note: Lot numbers are auto-generated by backend (format: YY-######)
   */
  create: async (data: CompanyCreateGroup): Promise<CompanyFullGroup> => {
    const response = await api.post(`${company_base}/`, data);
    return response.data;
  },
  list: async (
    params: ListRequest,
    company_type: CompanyType,
  ): Promise<PaginatedCompanyResponseGroup> => {
    const response = await api.post(
      `${company_base}/${company_type}/search`,
      params,
    );
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (
    id: string,
    company_type: CompanyType,
  ): Promise<CompanyFullGroup> => {
    const response = await api.get(`${company_base}/${company_type}/${id}`);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (
    id: string,
    data: CompanyUpdateGroup,
  ): Promise<CompanyFullGroup> => {
    const response = await api.put(`${company_base}/${id}`, { ...data, id });
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (
    id: string,
    company_type: CompanyType,
  ): Promise<MessageResponse> => {
    const response = await api.delete(`${company_base}/${company_type}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (
    id: string,
    company_type: CompanyType,
  ): Promise<CompanyFullGroup> => {
    const response = await api.post(
      `${company_base}/${company_type}/${id}/restore`,
    );
    return response.data;
  },
};

const person_base = "/people";
export const personService = {
  // ========================================
  // Core Inventory Lot Management (6 endpoints)
  // ========================================

  /**
   * Create a new inventory lot
   * POST /api/v1/inventory/lots
   * Note: Lot numbers are auto-generated by backend (format: YY-######)
   */
  create: async (data: PersonCreateGroup): Promise<PersonFullGroup> => {
    const response = await api.post(`${person_base}/`, data);
    return response.data;
  },
  list: async (
    params: ListRequest,
    person_type: PersonType,
  ): Promise<PaginatedPersonResponseGroup> => {
    const response = await api.post(
      `${person_base}/${person_type}/search`,
      params,
    );
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (
    id: string,
    person_type: PersonType,
  ): Promise<PersonFullGroup> => {
    const response = await api.get(`${person_base}/${person_type}/${id}`);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (
    id: string,
    data: PersonUpdateGroup,
  ): Promise<PersonFullGroup> => {
    const response = await api.put(`${person_base}/${id}`, { ...data, id });
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (
    id: string,
    person_type: PersonType,
  ): Promise<MessageResponse> => {
    const response = await api.delete(`${person_base}/${person_type}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (
    id: string,
    person_type: PersonType,
  ): Promise<PersonFullGroup> => {
    const response = await api.post(
      `${person_base}/${person_type}/${id}/restore`,
    );
    return response.data;
  },
};
